// The Opportunity Room — the non-Service-Advisor home view.
//
// Evolves the CEO's "Service Drive Intelligence Report" into a data room that
// makes sense for every department: one filter pipeline, a chapter per
// audience, and a signals engine that reads the room's own numbers ("the
// brief"). This file owns the pipeline and the layout; the math lives in
// derive.ts / derive-signals.ts and each chapter is a dumb, props-driven view.
//
// Reading data notes:
//   • `metadata.source` is the channel discriminator (mock-only today; real BE
//     doesn't stamp it — see design log). Missing source = service drive.
//   • Flow metrics respect the range control and compare to the previous equal
//     window; backlog metrics (open, aging, recovery queue, walkarounds) apply
//     the dimensional filters but ignore the range.
//   • Cross-filter emphasis: the widget that renders the active dimension is
//     computed with that one filter skipped, so all its values stay visible.

import { Fragment, useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router"
import { ZapFast, TrendUp01, ArrowUp } from "@untitledui/icons"
import { SetupTasksBanner } from "@/components/setup-tasks/setup-tasks-banner"
import { EstimatePeek } from "@/components/dashboard/estimate-peek"
import { NotificationsBell } from "@/components/dashboard/notifications-bell"
import { useEstimatesReport } from "@/lib/api/use-reporting"
import { useTeam, useTeamMembers } from "@/lib/api/use-teams"
import { usePayouts } from "@/lib/api/use-payouts"
import { useSalesLeadsPending, useSalesLeadsAssigned } from "@/lib/api/use-sales-leads"
import type { ApiEstimate, TeamSettingsMetadata, VehicleSection } from "@/lib/api/types"
import {
  CHAPTER_LABEL, HEADLINE, SECTION_LABELS, SOURCE_LABEL, SOURCES, TIERS,
  matchDim, roomConfig, sourceOf, type ChapterId, type DimKey, type SourceKey, type ViewMode,
} from "./room-model"
import {
  deriveHeadline, deriveChannelCards, deriveRecovery,
  deriveAdvisors, deriveCapture, deriveCategories, deriveProfit, deriveDistribution,
  deriveSections, deriveLaborHours, deriveVehicleMix, deriveHeatmap, deriveStores,
  derivePartsBreakdown, deriveMakeInsights, derivePaint, deriveEvaluations,
  deriveTrends, deriveActivity, deriveInactiveAdvisors,
  deriveDollarFlow, deriveFollowupFlags, deriveUsageSection, deriveEconomics, deriveDelivery,
  deriveHighlights, type HighlightAction,
  type AdvisorSortKey, type UsagePeriodKey, type Walkaround,
} from "./derive"
import { useRoomState } from "./use-room-state"
import { useScrollSpy } from "./use-scroll-spy"
import { DashboardSkeleton } from "./room-chrome"
import { ExpandProvider } from "./chart-expand"
import { CommandDeck } from "./command-deck"
import { VerdictStrip } from "./verdict-strip"
import { LaneChapter } from "./chapters/lane-chapter"
import { ConvertingChapter } from "./chapters/converting-chapter"
import { WorkChapter } from "./chapters/work-chapter"
import { PartsChapter } from "./chapters/parts-chapter"
import { MakesChapter } from "./chapters/makes-chapter"
import { PaintChapter } from "./chapters/paint-chapter"
import { PeopleChapter } from "./chapters/people-chapter"
import { StoresChapter } from "./chapters/stores-chapter"

/** Floating back-to-top — the director page is long; top = re-orientation. */
function BackToTop() {
  const [show, setShow] = useState(false)
  useEffect(() => {
    const m = document.querySelector("main")
    if (!m) return
    const on = () => setShow(m.scrollTop > 1400)
    m.addEventListener("scroll", on, { passive: true })
    return () => m.removeEventListener("scroll", on)
  }, [])
  if (!show) return null
  return (
    <button onClick={() => document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
      className="fixed bottom-24 right-5 z-40 size-10 rounded-full bg-zinc-900/90 text-white shadow-lg grid place-items-center hover:bg-black transition-colors print:hidden">
      <ArrowUp className="size-4" aria-hidden />
    </button>
  )
}

export function OpportunityRoom({ estimates, isLoading, isGroupView, teamId, viewMode, firstName, greet, today, role, teamLabel, canQuick }: {
  estimates: ApiEstimate[]
  isLoading: boolean
  isGroupView: boolean
  teamId: string | number | null
  viewMode: ViewMode
  firstName: string
  greet: string
  today: string
  role: string
  teamLabel: string | null
  canQuick: boolean
}) {
  const landing = roomConfig(viewMode, true).chapters[0]
  const state = useRoomState({ defaultChapter: landing })
  const { f: fBase, rangeDef } = state
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [peeked, setPeeked] = useState<ApiEstimate | null>(null)
  const [advisorSort, setAdvisorSort] = useState<{ key: AdvisorSortKey; desc: boolean }>({ key: "identified", desc: true })

  // Walkarounds ride on the row-74 report (mock-only field; group view falls
  // back to estimate-derived counts).
  const { data: report } = useEstimatesReport(isGroupView ? null : teamId, null, null)
  const allWalk = useMemo<Walkaround[] | null>(() => {
    if (!report?.walkarounds) return null
    return report.walkarounds.map(w => ({
      id: w.id, vehicleId: w.vehicleId,
      source: (SOURCES.some(s => s.key === w.source) ? w.source : "service_drive") as SourceKey,
      dealershipId: w.dealershipId != null ? String(w.dealershipId) : null,
      advisorId: w.advisorId != null ? String(w.advisorId) : null,
      estimateId: w.estimateId, createdAt: w.createdAt,
    }))
  }, [report])

  const channelLabel = SOURCE_LABEL(fBase.source)?.toLowerCase() ?? "all channels"
  const channelTitle = fBase.source ? (SOURCE_LABEL(fBase.source) ?? "Service drive") : "All-channel"

  /* ── Filter pipeline ── */
  const usable = useMemo(() => estimates.filter(e => e.status !== "Draft" && e.status !== "Loading"), [estimates])

  // Who ran each estimate — the rosters supply role + department (a user's
  // identity is name · role · department wherever an author is shown/picked).
  // Estimates cross the dealership ⇄ body-shop boundary, so the counterparty
  // roster loads too — its id comes from the estimates themselves.
  const counterTeamId = useMemo(() => {
    const tid = teamId != null && !isGroupView ? Number(teamId) : null
    for (const e of usable) {
      const d = e.dealership?.id
      const b = e.bodyshop?.id
      if (d != null && d !== tid) return d
      if (b != null && b !== tid) return b
    }
    return null
  }, [usable, teamId, isGroupView])
  const { data: ownMembers = [] } = useTeamMembers(
    teamId != null && !isGroupView ? Number(teamId) : null,
  )
  const { data: counterMembers = [] } = useTeamMembers(counterTeamId)
  const memberMeta = useMemo(
    () =>
      new Map(
        [...ownMembers, ...counterMembers].map(m => [
          String(m.id),
          { role: m.role, department: m.department },
        ]),
      ),
    [ownMembers, counterMembers],
  )
  const advisorOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const e of usable) {
      const c = e.createdBy
      if (c?.id == null) continue
      seen.set(String(c.id), `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() || `User #${c.id}`)
    }
    return [...seen.entries()]
      .map(([id, name]) => {
        const m = memberMeta.get(id)
        return {
          id,
          name,
          sub: m ? `${m.role}${m.department ? ` · ${m.department}` : ""}` : undefined,
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [usable, memberMeta])
  // Departments that actually authored something — pickable as a filter too.
  const departmentOptions = useMemo(() => {
    const set = new Set<string>()
    for (const a of advisorOptions) {
      const d = memberMeta.get(a.id)?.department
      if (d) set.add(d)
    }
    return [...set].sort()
  }, [advisorOptions, memberMeta])

  // "advisor" URL param holds a person id OR "dept:X" — resolve a department
  // to its member-id set so every matchDim call narrows to the whole group.
  const f = useMemo<typeof fBase>(() => {
    if (!state.advisorId.startsWith("dept:")) return fBase
    const dept = state.advisorId.slice(5)
    const ids = new Set(
      [...memberMeta.entries()].filter(([, m]) => m.department === dept).map(([id]) => id),
    )
    return { ...fBase, advisorIdSet: ids }
  }, [fBase, state.advisorId, memberMeta])
  const windowMs = rangeDef.days * 86400000
  const inWindow = useMemo(() => {
    const from = Date.now() - windowMs
    return (e: ApiEstimate) => new Date(e.createdAt).getTime() >= from
  }, [windowMs])

  const inScope = useMemo(() => usable.filter(e => matchDim(e, f)), [usable, f])
  const filtered = useMemo(() => inScope.filter(inWindow), [inScope, inWindow])
  const previousWindow = useMemo(() => {
    const now = Date.now()
    return inScope.filter(e => {
      const t = new Date(e.createdAt).getTime()
      return t >= now - 2 * windowMs && t < now - windowMs
    })
  }, [inScope, windowMs])

  const scopeSkipping = (skip: DimKey) => usable.filter(e => matchDim(e, f, skip)).filter(inWindow)
  const forTiers = useMemo(() => scopeSkipping("tier"), [usable, f, inWindow]) // eslint-disable-line react-hooks/exhaustive-deps
  const forSections = useMemo(() => scopeSkipping("section"), [usable, f, inWindow]) // eslint-disable-line react-hooks/exhaustive-deps
  const forAdvisors = useMemo(() => scopeSkipping("advisor"), [usable, f, inWindow]) // eslint-disable-line react-hooks/exhaustive-deps
  const forStores = useMemo(() => scopeSkipping("store"), [usable, f, inWindow]) // eslint-disable-line react-hooks/exhaustive-deps
  const forChannels = useMemo(() => scopeSkipping("source"), [usable, f, inWindow]) // eslint-disable-line react-hooks/exhaustive-deps
  const forMakes = useMemo(() => scopeSkipping("make"), [usable, f, inWindow]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Walkaround scoping (backlog-style: dims apply, but windowed for trend) ── */
  const walkScoped = useMemo(() => (allWalk ?? []).filter(w => {
    if (f.source && w.source !== f.source) return false
    if (f.storeId && (w.dealershipId ?? "") !== f.storeId) return false
    if (f.advisorId) {
      const wid = w.advisorId ?? ""
      if (f.advisorIdSet) {
        if (!f.advisorIdSet.has(wid)) return false
      } else if (wid !== f.advisorId) return false
    }
    return true
  }), [allWalk, f])
  const walkInWindow = useMemo(() => {
    const from = Date.now() - windowMs
    return walkScoped.filter(w => new Date(w.createdAt).getTime() >= from)
  }, [walkScoped, windowMs])
  const walkPrev = useMemo(() => {
    const now = Date.now()
    return walkScoped.filter(w => { const t = new Date(w.createdAt).getTime(); return t >= now - 2 * windowMs && t < now - windowMs })
  }, [walkScoped, windowMs])

  /* ── Derived datasets ── */
  const d = useMemo(() => deriveHeadline({ filtered, previousWindow, inScope, windowMs, buckets: rangeDef.buckets, allChannels: f.source === null }), [filtered, previousWindow, inScope, windowMs, rangeDef.buckets, f.source])
  const channelCards = useMemo(() => deriveChannelCards(forChannels), [forChannels])
  const recovery = useMemo(() => deriveRecovery(inScope), [inScope])
  const advisors = useMemo(() => deriveAdvisors(forAdvisors, advisorSort), [forAdvisors, advisorSort])
  const captureData = useMemo(() => deriveCapture(d), [d])
  const categories = useMemo(() => deriveCategories(filtered), [filtered])
  const profit = useMemo(() => deriveProfit(categories), [categories])
  const distribution = useMemo(() => deriveDistribution(forTiers, state.tier), [forTiers, state.tier])
  const sectionsData = useMemo(() => deriveSections(forSections, state.section), [forSections, state.section])
  const laborHours = useMemo(() => deriveLaborHours(filtered), [filtered])
  const vehicleMix = useMemo(() => deriveVehicleMix(filtered), [filtered])
  const heatmap = useMemo(() => deriveHeatmap(filtered), [filtered])
  const stores = useMemo(() => deriveStores(forStores, usable, f, isGroupView), [forStores, usable, f, isGroupView])
  const partsBreakdown = useMemo(() => derivePartsBreakdown(forSections), [forSections])
  const makeInsights = useMemo(() => deriveMakeInsights(forMakes, d.avgValue, state.make), [forMakes, d.avgValue, state.make])
  const paint = useMemo(() => derivePaint(filtered), [filtered])
  const evaluations = useMemo(() => deriveEvaluations({ inWindow: allWalk ? walkInWindow : null, prevWindow: allWalk ? walkPrev : null, estimateCount: d.total }), [allWalk, walkInWindow, walkPrev, d.total])
  const trends = useMemo(() => deriveTrends(filtered, previousWindow, rangeDef.buckets, windowMs), [filtered, previousWindow, rangeDef.buckets, windowMs])
  const flow = useMemo(() => deriveDollarFlow(filtered), [filtered])
  const flags = useMemo(() => deriveFollowupFlags(inScope), [inScope])
  const delivery = useMemo(() => deriveDelivery(inScope, filtered), [inScope, filtered])

  /* ── Adoption pulse (for the action band + Who & when) ── */
  const activity = useMemo(() => deriveActivity(inScope), [inScope])
  const weekActiveIds = useMemo(() => {
    const wk = Date.now() - 7 * 86400000
    return new Set(inScope.filter(e => e.createdBy && new Date(e.createdAt).getTime() >= wk).map(e => String(e.createdBy!.id)))
  }, [inScope])
  const inactive = useMemo(() => deriveInactiveAdvisors(report?.perServiceAdvisor, weekActiveIds), [report?.perServiceAdvisor, weekActiveIds])

  /* ── Usage & adoption section (the CEO's usage visuals — own period tabs) ── */
  const { data: team } = useTeam(isGroupView ? null : teamId)
  const goalsRewards = ((team?.metadata ?? {}) as TeamSettingsMetadata).goalsRewards
  const usageSection = useMemo(() => deriveUsageSection({
    usable, walkarounds: allWalk,
    roster: report?.perServiceAdvisor,
    goals: {
      weeklyTarget: goalsRewards?.weeklyEstimateTarget ?? null,
      monthlyTarget: goalsRewards?.monthlyEstimateTarget ?? null,
      avgDailyLaneROs: goalsRewards?.avgDailyLaneROs ?? null,
    },
    period: (["today", "wtd", "mtd", "all"].includes(state.usage) ? state.usage : "wtd") as UsagePeriodKey,
  }), [usable, allWalk, report?.perServiceAdvisor, goalsRewards, state.usage])

  /* ── Per-advisor monthly goal (the reporting half of per-advisor goals —
        surface to the manager who set them, #30). A per-advisor override wins;
        otherwise the team monthly target ÷ advisor headcount. It's the GOAL,
        not a period-scoped pace, so it stays honest next to range-scoped
        actuals. ── */
  const perAdvisorMonthlyGoals = useMemo(() => {
    const out: Record<string, number> = {}
    const overrides = goalsRewards?.perAdvisorGoals ?? {}
    for (const [id, v] of Object.entries(overrides)) {
      const m = (v as { monthlyEstimateTarget?: number })?.monthlyEstimateTarget
      if (m != null && m > 0) out[id] = m
    }
    return out
  }, [goalsRewards])
  const defaultMonthlyGoal = useMemo(() => {
    const monthly = goalsRewards?.monthlyEstimateTarget ?? null
    const count = report?.perServiceAdvisor?.length ?? 0
    return monthly != null && monthly > 0 && count > 0 ? Math.ceil(monthly / count) : null
  }, [goalsRewards, report?.perServiceAdvisor])

  /* ── Program economics (leadership view only) ── */
  const numericTeamId = teamId != null && !isGroupView ? Number(teamId) : null
  const { data: payouts } = usePayouts(viewMode === "full" ? numericTeamId : null)
  const { data: tlPending } = useSalesLeadsPending(viewMode === "full" ? numericTeamId : null)
  const { data: tlAssigned } = useSalesLeadsAssigned(viewMode === "full" ? numericTeamId : null)
  const economics = useMemo(() => {
    if (viewMode !== "full") return null
    const payoutTotal = payouts ? payouts.reduce((s, p) => s + (p.approvedAmount ?? p.computedAmount), 0) : null
    return deriveEconomics({
      usable, payoutTotal,
      totalLossLeadsPending: tlPending ? tlPending.length : null,
      totalLossLeadsAssigned: tlAssigned ? tlAssigned.length : null,
      walkarounds: allWalk,
    })
  }, [viewMode, usable, payouts, tlPending, tlAssigned, allWalk])

  /* ── Active filter chips (channel filtering lives on the lane's cards; the
        active channel appears here as a removable chip) ── */
  const chips = useMemo(() => {
    const out: { key: string; label: string; clear: () => void }[] = []
    if (f.source) out.push({ key: "source", label: `Channel: ${SOURCE_LABEL(f.source)}`, clear: () => state.setSource("all") })
    if (state.advisorId) {
      if (state.advisorId.startsWith("dept:")) {
        out.push({ key: "advisor", label: `Department: ${state.advisorId.slice(5)}`, clear: () => state.setAdvisorId("") })
      } else {
        const nm = usable.find(e => String(e.createdBy?.id ?? "") === state.advisorId)?.createdBy
        out.push({ key: "advisor", label: `Advisor: ${nm ? `${nm.firstName} ${nm.lastName}` : state.advisorId}`, clear: () => state.setAdvisorId("") })
      }
    }
    if (state.storeId) {
      const nm = usable.find(e => String(e.dealership?.id ?? "") === state.storeId)?.dealership?.name
      out.push({ key: "store", label: `Store: ${nm ?? state.storeId}`, clear: () => state.setStoreId("") })
    }
    if (state.tier !== "") out.push({ key: "tier", label: `Value: ${TIERS[Number(state.tier)]?.label ?? state.tier}`, clear: () => state.setTier("") })
    if (state.section) out.push({ key: "section", label: `Section: ${SECTION_LABELS[state.section as VehicleSection] ?? state.section}`, clear: () => state.setSection("") })
    if (state.make) out.push({ key: "make", label: `Make: ${state.make}`, clear: () => state.setMake("") })
    return out
  }, [state, usable, f.source])

  const pendingCount = useMemo(() => inScope.filter(e => e.status === "Pending" || e.status === "Loading").length, [inScope])

  /* ── Highlights (the analyst voice) + receipts handler ── */
  const highlights = useMemo(() => deriveHighlights({
    total: d.total, flow, distribution, parts: partsBreakdown, makes: makeInsights,
    paint, usage: usageSection, stores,
  }), [d.total, flow, distribution, partsBreakdown, makeInsights, paint, usageSection, stores])

  const scrollToQueue = () => document.getElementById("recovery-queue")?.scrollIntoView({ behavior: "smooth", block: "center" })
  // A highlight click applies the filter AND lands on receipts (the queue rows
  // are dimension-filtered, so the reader sees the actual cars).
  const onHighlight = (a: HighlightAction) => {
    switch (a.type) {
      case "tier": state.applyScope({ tier: state.tier === a.value ? null : a.value }); requestAnimationFrame(scrollToQueue); break
      case "make": state.applyScope({ make: state.make === a.value ? null : a.value }); requestAnimationFrame(scrollToQueue); break
      case "advisor": state.applyScope({ advisor: state.advisorId === a.value ? null : a.value }); requestAnimationFrame(scrollToQueue); break
      case "store": state.applyScope({ store: state.storeId === a.value ? null : a.value }); requestAnimationFrame(scrollToQueue); break
      case "section": state.applyScope({ section: state.section === a.value ? null : a.value }); requestAnimationFrame(scrollToQueue); break
      case "partsView": state.setParts("component"); break
      case "queue": scrollToQueue(); break
    }
  }

  /* ── Chapters present (role-tailored) + scrollspy ── */
  const showStores = isGroupView && stores.rows.length > 1
  const roleChapters = useMemo(() => roomConfig(viewMode, showStores).chapters, [viewMode, showStores])
  const ready = !isLoading && usable.length > 0
  const { scrollToChapter } = useScrollSpy({ containerRef: scrollRef, chapter: state.chapter, setChapter: state.setChapter, chapterIds: roleChapters, ready })

  const onNavClick = (id: ChapterId) => { state.setChapter(id); scrollToChapter(id) }
  const onPrint = () => window.print()

  // Render one chapter by id (role order controls sequence; `first` drops the
  // top divider on the leading chapter).
  const renderChapter = (id: ChapterId, first: boolean) => {
    switch (id) {
      case "lane":
        return (
          <LaneChapter
            first={first} d={d} profit={profit} evaluations={evaluations} channelCards={channelCards}
            economics={economics} afterHoursConsumer={delivery.afterHoursConsumer}
            allChannels={f.source === null} channelLabel={channelLabel} channelTitle={channelTitle}
            rangeLabel={rangeDef.label} onSource={state.setSource}
            hasFilters={chips.length > 0} filteredEmpty={filtered.length === 0} onClearDims={state.clearDims}
          />
        )
      case "converting":
        return (
          <ConvertingChapter
            d={d} captureData={captureData} flow={flow} flags={flags} trends={trends}
            highlight={highlights.flow} onHighlight={onHighlight}
            recoveryList={recovery.list} recoveryTotal={recovery.total} onPeek={setPeeked}
            channelLabel={channelLabel} rangeLabel={rangeDef.label}
          />
        )
      case "work":
        return (
          <WorkChapter
            d={d} categories={categories} profit={profit} distribution={distribution}
            highlight={highlights.distribution} onHighlight={onHighlight}
            sectionsData={sectionsData} laborHours={laborHours} delivery={delivery} tier={state.tier} section={state.section}
            onToggleTier={state.toggleTier} onToggleSection={state.toggleSection} rangeLabel={rangeDef.label}
          />
        )
      case "parts":
        return (
          <PartsChapter
            parts={partsBreakdown} view={state.parts} onSetView={state.setParts}
            highlight={highlights.parts} onHighlight={onHighlight}
            section={state.section} onToggleSection={state.toggleSection}
            closeRate={d.closeRate} adasDollars={delivery.adasDollars} rangeLabel={rangeDef.label}
          />
        )
      case "makes":
        return <MakesChapter makes={makeInsights} vehicleMix={vehicleMix} make={state.make} onToggleMake={state.toggleMake} highlight={highlights.makes} onHighlight={onHighlight} rangeLabel={rangeDef.label} />
      case "paint":
        return <PaintChapter paint={paint} highlight={highlights.paint} onHighlight={onHighlight} rangeLabel={rangeDef.label} />
      case "people":
        return (
          <PeopleChapter
            first={first} usage={usageSection} onUsagePeriod={p => state.setUsage(p)}
            highlight={highlights.leaderboard} onHighlight={onHighlight}
            advisorRows={advisors.rows} maxSubmitted={advisors.maxSubmitted}
            advisorSort={advisorSort} onSort={key => setAdvisorSort(s => ({ key, desc: s.key === key ? !s.desc : true }))}
            onToggleAdvisor={state.toggleAdvisor} advisorId={state.advisorId}
            memberMeta={memberMeta}
            perAdvisorMonthlyGoals={perAdvisorMonthlyGoals} defaultMonthlyGoal={defaultMonthlyGoal}
            heatmap={heatmap} channelLabel={channelLabel} rangeLabel={rangeDef.label}
          />
        )
      case "stores":
        return showStores ? <StoresChapter stores={stores} storeId={state.storeId} onToggleStore={state.toggleStore} highlight={highlights.stores} onHighlight={onHighlight} /> : null
      default:
        return null
    }
  }

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <div className="max-w-[1500px] mx-auto"><DashboardSkeleton /></div>
      </div>
    )
  }

  return (
    <ExpandProvider>
      <div ref={scrollRef} className="room-scroll min-h-full bg-[#FAFAF8] p-4 md:p-6 lg:p-8">
        <div className="max-w-[1500px] mx-auto">

          {/* Print-only report header — the room, exported as the Intelligence Report */}
          <div className="hidden print:block mb-6">
            <p className="text-2xl font-bold text-zinc-900" style={HEADLINE}>Service Drive Intelligence Report</p>
            <p className="text-sm text-[#6E6C64] mt-1">
              {channelTitle} · last {rangeDef.label}{chips.length ? ` · ${chips.map(c => c.label).join(" · ")}` : ""} · generated {today}
            </p>
          </div>

          {/* Header */}
          <div className="animate-element mb-5 print:hidden">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div>
                <p className="text-[13px] text-[#8B897E] font-medium mb-1">{today}</p>
                <h1 className="text-2xl md:text-3xl font-bold text-zinc-900 tracking-tight" style={HEADLINE}>{greet}, {firstName}</h1>
                <p className="text-sm text-[#8B897E] mt-1">{[role, teamLabel].filter(Boolean).join(" · ")} · Real collision opportunity, {channelLabel}</p>
              </div>
              <div className="flex items-center gap-2.5 pt-4 shrink-0">
                {/* Body shop: consumer leads and status moves land in the
                    activity bell — same feed the advisor dashboard polls. */}
                {viewMode === "bodyshop" && numericTeamId != null && (
                  <NotificationsBell teamId={numericTeamId} />
                )}
                {canQuick && (
                  <Link to="/estimates/quick?return=/dashboard" className="hidden md:flex items-center gap-2 h-10 px-5 rounded-xl border border-[#E3E1DA] bg-white text-sm font-medium text-zinc-700 hover:border-[#1D9E75] hover:bg-[#E8F8F2] transition-all duration-150">
                    <ZapFast className="size-4 text-[#1D9E75]" aria-hidden /> Quick Estimate
                  </Link>
                )}
                <Link to="/reports" className="hidden md:flex items-center gap-1.5 h-10 px-5 rounded-xl bg-zinc-900 text-sm font-medium text-white hover:bg-black transition-all duration-150">
                  <TrendUp01 className="size-4" aria-hidden /> Reports
                </Link>
              </div>
            </div>
          </div>

          <SetupTasksBanner className="animate-element mb-4 print:hidden" />

          {usable.length === 0 ? (
            <div className="animate-element rounded-2xl border border-dashed border-zinc-300 bg-white p-14 text-center">
              <p className="text-lg font-semibold text-zinc-900 mb-1" style={HEADLINE}>No opportunity captured yet</p>
              <p className="text-sm text-[#8B897E] max-w-md mx-auto mb-6">The room fills in as your team scans vehicles. Start with a quick estimate in the service lane — it takes under a minute.</p>
              {canQuick && (
                <Link to="/estimates/quick?return=/dashboard" className="inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-zinc-900 text-sm font-medium text-white hover:bg-black transition-all duration-150">
                  <ZapFast className="size-4 text-[#5DCAA5]" aria-hidden /> New Quick Estimate
                </Link>
              )}
            </div>
          ) : (
            <>
              {/* Row one — the verdict: NUMBER → MEANING → ACTION, role-true. */}
              <VerdictStrip
                viewMode={viewMode} d={d} profit={profit} allChannels={f.source === null} rangeLabel={rangeDef.label}
                agingCount={d.agingCount} aging48Count={d.aging48Count} agingValue={d.agingValue}
                pendingCount={pendingCount} activity={activity} quietCount={inactive.quiet.length}
                economics={economics} onGo={onNavClick}
              />

              <CommandDeck
                chapters={roleChapters.map(id => ({ id, label: CHAPTER_LABEL[id] }))}
                activeChapter={state.chapter}
                onNavClick={onNavClick}
                rangeLabel={rangeDef.label}
                onRange={state.setRange}
                advisors={advisorOptions}
                departments={departmentOptions}
                advisorId={state.advisorId}
                onAdvisor={state.setAdvisorId}
                chips={chips}
                onClearDims={state.clearDims}
                onPrint={onPrint}
                scrollContainerRef={scrollRef}
              />

              {/* One full-width flow, left to right — the CEO's document structure */}
              <div className="min-w-0">
                {roleChapters.map((id, i) => <Fragment key={id}>{renderChapter(id, i === 0)}</Fragment>)}
              </div>

              {/* Methodology footer */}
              <div className="mt-8 rounded-xl bg-[#F1F0EB] border border-[#ECEBE6] px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
                <p className="text-[11px] text-[#6E6C64] leading-relaxed max-w-3xl">
                  This analysis was conducted by eTX ImpaXt personnel using on-site vehicle walkaround assessments. Estimates reflect observed collision damage and do not represent insured claims or approved repair orders.
                </p>
                <span className="text-[13px] font-bold text-[#0F6E56]" style={HEADLINE}>eTX ImpaXt</span>
              </div>
            </>
          )}
        </div>

        <EstimatePeek estimate={peeked} sourceLabel={peeked ? SOURCE_LABEL(sourceOf(peeked)) : null} onClose={() => setPeeked(null)} />
        <BackToTop />
      </div>
    </ExpandProvider>
  )
}
